import { useState, useMemo, useCallback } from 'react';
import { AppTable } from '@/shared/ui/AppTable';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { ModalShell } from '@/shared/ui/ModalShell';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { semantic, typography, shadow, radius } from '@/shared/theme/tokens';
import { useToast, useApiErrorToast } from '@/shared/ui/Toast';
import {
  useNotifications, useDeleteNotification, useToggleNotification, useNotificationEvents,
} from '@/features/notifications/hooks/useNotifications';
import {
  CHANNELS, STATUS_META, recipientSummary, triggerSummary,
} from '@/features/notifications/notifications.meta';
import { NotificationForm } from './NotificationForm';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function NotificationsTab({ deploymentId, canManage }) {
  const c = useThemeColors();
  const { data: notifications = [], isLoading } = useNotifications(deploymentId);
  const { byKey: eventByKey } = useNotificationEvents();
  const deleteNotification = useDeleteNotification(deploymentId);
  const toggleNotification = useToggleNotification(deploymentId);
  const toast = useToast();
  const toastApiError = useApiErrorToast();

  // 'list' | { mode: 'create' } | { mode: 'edit', notification }
  const [view, setView] = useState('list');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [togglingId, setTogglingId] = useState(null);   // row currently flipping

  const getRowId = useCallback((row) => row.id, []);

  async function handleToggle(row) {
    if (togglingId) return;   // ignore clicks while a toggle is in flight
    setTogglingId(row.id);
    try {
      await toggleNotification.mutateAsync({ id: row.id, enabled: !row.enabled });
      toast({ message: `Notification ${row.enabled ? 'paused' : 'activated'}` });
    } catch (err) { toastApiError(err); }
    finally { setTogglingId(null); }
  }

  async function handleDelete(row) {
    try {
      await deleteNotification.mutateAsync(row.id);
      setDeleteTarget(null);
      toast({ message: 'Notification deleted' });
    } catch (err) { toastApiError(err); }
  }

  const columns = useMemo(() => {
    const base = [
      {
        field: 'name', headerName: 'Notification', flex: 1.6, minWidth: 220,
        renderCell: ({ row }) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 0', minWidth: 0 }}>
            <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</span>
            {row.description && (
              <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description}</span>
            )}
          </div>
        ),
      },
      {
        field: 'channel', headerName: 'Channel', flex: 0.7, minWidth: 110,
        renderCell: ({ row }) => {
          const def = CHANNELS[row.channel] ?? {};
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: typography.fontSize.base, color: c.textLabel }}>
              <span className="material-symbols-outlined" style={{ fontSize: 15, color: c.textSubtle }}>{def.icon}</span>
              {def.label ?? row.channel}
            </span>
          );
        },
      },
      {
        field: 'event', headerName: 'Event / Trigger', flex: 1.3, minWidth: 190,
        renderCell: ({ row }) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 0', minWidth: 0 }}>
            <span style={{ fontSize: typography.fontSize.md, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {eventByKey[row.event]?.label ?? row.event ?? '—'}
            </span>
            <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{triggerSummary(row)}</span>
          </div>
        ),
      },
      {
        field: '__recipients', headerName: 'Recipients', flex: 0.9, minWidth: 130, sortable: false,
        renderCell: ({ row }) => <span style={{ fontSize: typography.fontSize.base, color: c.textLabel }}>{recipientSummary(row)}</span>,
      },
      {
        field: '__status', headerName: 'Status', flex: 0.6, minWidth: 100, sortable: false,
        renderCell: ({ row }) => <StatusChip enabled={row.enabled} />,
      },
      {
        field: 'updatedAt', headerName: 'Updated', flex: 0.7, minWidth: 110,
        renderCell: ({ value }) => <span style={{ fontSize: typography.fontSize.base, color: c.textSubtle }}>{fmtDate(value)}</span>,
      },
    ];

    if (canManage) {
      base.push({
        field: '__actions', headerName: 'Actions', width: 150, sortable: false, filterable: false, disableColumnMenu: true,
        renderCell: ({ row }) => (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <ToggleSwitch on={row.enabled} loading={togglingId === row.id} disabled={!!togglingId} onChange={() => handleToggle(row)} c={c} title={row.enabled ? 'Pause' : 'Activate'} />
            <button onClick={() => setView({ mode: 'edit', notification: row })} style={actionBtnStyle(c)} title="Edit">
              <span className="material-symbols-outlined" style={{ fontSize: 14, color: c.textSubtle }}>edit</span>
            </button>
            <button onClick={() => setDeleteTarget(row)} style={{ ...actionBtnStyle(c), borderColor: semantic.errorBorder }} title="Delete">
              <span className="material-symbols-outlined" style={{ fontSize: 14, color: semantic.error }}>delete</span>
            </button>
          </div>
        ),
      });
    }
    return base;
  }, [canManage, c, eventByKey, togglingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Form view (full page, in place of the table) ──
  if (view !== 'list') {
    return (
      <NotificationForm
        mode={view.mode}
        notification={view.notification}
        deploymentId={deploymentId}
        onClose={() => setView('list')}
      />
    );
  }

  // ── List view ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, fontFamily: typography.fontFamily.body }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: typography.fontSize.base, color: c.textSubtle }}>
          {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
        </span>
        <span style={{ flex: 1 }} />
        {canManage && (
          <PrimaryButton icon="add" size="sm" onClick={() => setView({ mode: 'create' })}>
            Add Notification
          </PrimaryButton>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.xl, overflow: 'hidden' }}>
        <AppTable rows={notifications} columns={columns} getRowId={getRowId} loading={isLoading} getRowHeight={() => 'auto'} />
      </div>

      {deleteTarget && (
        <ConfirmDeleteModal
          notification={deleteTarget}
          loading={deleteNotification.isPending}
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
          c={c}
        />
      )}
    </div>
  );
}

// Read-only status indicator — communicates state, not an action.
function StatusChip({ enabled }) {
  const meta = enabled ? STATUS_META.active : STATUS_META.paused;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', borderRadius: radius.sm,
      background: meta.bg, border: `1px solid ${meta.border}`, color: meta.color, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
      {meta.label}
    </span>
  );
}

// Clearly-interactive on/off switch for enable/pause.
function ToggleSwitch({ on, onChange, c, title, loading, disabled }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      title={loading ? 'Saving…' : title}
      role="switch"
      aria-checked={on}
      aria-busy={loading}
      style={{
        position: 'relative', width: 36, height: 20, borderRadius: radius.full, border: 'none',
        cursor: disabled ? 'wait' : 'pointer', padding: 0, flexShrink: 0,
        background: on ? semantic.successGreen : c.borderInput, transition: 'background 0.15s',
        opacity: disabled && !loading ? 0.6 : 1,
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: '50%',
        background: '#fff', transition: 'left 0.15s', boxShadow: shadow.search,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {loading && (
          <span className="material-symbols-outlined" style={{
            fontSize: 12, color: on ? semantic.successGreen : c.textSubtle, animation: 'ntf-spin 0.7s linear infinite',
          }}>progress_activity</span>
        )}
      </span>
      <style>{'@keyframes ntf-spin { to { transform: rotate(360deg); } }'}</style>
    </button>
  );
}

function ConfirmDeleteModal({ notification, loading, onConfirm, onCancel, c }) {
  return (
    <ModalShell onClose={onCancel} icon="delete" iconVariant="danger" title="Delete notification" maxWidth="420px">
      <p style={{ fontSize: typography.fontSize.lg, color: c.textLabel, margin: '0 0 20px', lineHeight: 1.5 }}>
        Delete <strong>{notification.name}</strong>? Recipients will stop receiving this notification.
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <PrimaryButton variant="secondary" onClick={onCancel} disabled={loading}>Cancel</PrimaryButton>
        <PrimaryButton variant="danger" onClick={onConfirm} disabled={loading} icon="delete">
          {loading ? 'Deleting…' : 'Delete'}
        </PrimaryButton>
      </div>
    </ModalShell>
  );
}

function actionBtnStyle(c) {
  return {
    width: 28, height: 28, borderRadius: 7, background: 'transparent', border: `1px solid ${c.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, flexShrink: 0,
  };
}
